/**
 * The SFX and engine data (audio-direction §2, §3, §7; D-044): `sfx.json` validated with Zod. Every combat sound
 * is a layered synthesis recipe (sfx/layers.ts) plus mixing data; the engine, spatial, distant-band, ship-sound,
 * alarm and lock-tone tuning live here too. UI sounds stay ZzFX parameter arrays (`ui`). The parsed object is
 * mutable so the audition page can tune it live.
 */
import { z } from 'zod'
import { layerSchema } from './sfx/layers'
import { ZZFX_PARAM_COUNT } from './zzfx'
import sfxJson from './sfx.json'

/** Every synthesized sound. The array index is the sound's runtime index. */
export const SOUND_IDS = [
  'pdc',
  'laser',
  'torpedo',
  'lockBeep',
  'lockSolid',
  'shieldHit',
  'hullHit',
  'explosionSmall',
  'explosionLarge',
  'boostStart',
  'boostLoop',
  'driveLoop',
  'rcsPuff',
  'thumpSmall',
  'thumpLarge',
  'sensorTick',
  'alertHullLow',
  'alertShieldDown',
  'alertIncomingLock',
  'alertWaveStart',
  'alertWaveCleared',
  // M3 (R11): the Reach's travel, scan and relink sounds. Append only (the array index is the runtime index).
  'reachCruiseCharge',
  'reachCruiseEngage',
  'reachCruise',
  'reachCruiseDrop',
  'reachMassLockDeny',
  'reachInterdictAlarm',
  'reachInterdictEscape',
  'reachTunnelSwell',
  'reachScanCharge',
  'reachScanPing',
  'reachPoiReveal',
  'reachRelayOnline',
  'reachRebootAlarm',
  // M2 (mining, salvage, docking; M2-21). Appended: never reorder (the array index is the runtime index).
  'miningBeamLoop',
  'miningStrainLoop',
  'chipBreak',
  'rockCrack',
  'rockBurst',
  'overheat',
  'cuttingBeamLoop',
  'anchorCut',
  'coolantVent',
  'tractorLoop',
  'partSecured',
  'chunkCollect',
  'scanSweep',
  'scanDone',
  'clearanceGranted',
  'clearanceExpired',
  'dockClamp',
  'undockRelease',
  'holdFull',
] as const
export type SoundId = (typeof SOUND_IDS)[number]

/** ZzFX UI sounds (kept from M1: "ZzFX stays for UI"). */
export const UI_SOUND_IDS = ['uiClick'] as const
export type UiSoundId = (typeof UI_SOUND_IDS)[number]

const soundIdSchema = z.enum(SOUND_IDS)
const positive = z.number().positive()
const nonNegative = z.number().nonnegative()
const unit = z.number().min(0).max(1)
const db = z.number().min(-60).max(24)

export const mixSchema = z.object({
  /** Linear gain per play (after peak normalisation), before distance and bus gains. */
  gain: z.number().min(0).max(4),
  /** Simultaneous voices of this sound; a play past the cap steals this sound's oldest voice. */
  voices: z.number().int().min(1).max(16),
  /** Pre-rendered variations (audio-direction §2: 2–4 per effect); plays cycle through them. */
  variants: z.number().int().min(1).max(4),
  /** Random playback-rate spread per play (±fraction): 0.03 = ±3 % pitch. */
  pitchJitter: z.number().min(0).max(0.2).default(0.03),
  /** Random gain spread per play (±dB). */
  gainJitterDb: z.number().min(0).max(6).default(1.5),
  /** What a 2–20 km event of this sound becomes: a muffled thump (+ a sensor tick), or nothing. */
  distant: z.object({ sound: soundIdSchema, gain: z.number().min(0).max(4), tick: z.boolean().default(false) }).nullable().default(null),
  /** A big impact: ducks the music −4 dB for 400 ms (audio-direction §4.6). */
  duck: z.boolean().default(false),
})

/** A synthesis recipe: layers rendered offline (also used by the score's BRAAAM and Shepard riser). */
export const recipeSchema = z.object({
  /** Rendered length (s). Loops render `seconds + loop.crossfade` and fold the tail over the head. */
  seconds: z.number().positive().max(12),
  channels: z.union([z.literal(1), z.literal(2)]).default(1),
  layers: z.array(layerSchema).min(1).max(24),
  loop: z.object({ crossfade: z.number().positive().max(2) }).optional(),
  /** Peak level every variation of the sound is normalised to (dBFS; all variations share one factor). */
  normalizeDb: z.number().min(-40).max(0).default(-1),
})

export const soundDefSchema = recipeSchema.extend({ mix: mixSchema })

const uiSoundSchema = z.object({ zzfx: z.array(z.number().finite()).min(1).max(ZZFX_PARAM_COUNT), gain: z.number().min(0).max(4), voices: z.number().int().min(1).max(8) })

const compressorSchema = z.object({
  thresholdDb: z.number().min(-100).max(0),
  kneeDb: z.number().min(0).max(40),
  ratio: z.number().min(1).max(20),
  attack: z.number().min(0).max(1),
  release: z.number().min(0).max(1),
})

/** A one-shot the M2 router plays for an event that has no recipe of its own (a reused sound, re-pitched). */
const cueSchema = z.object({ sound: soundIdSchema, gain: z.number().min(0).max(2), rate: z.number().min(0.25).max(4) })

/** M2 audio tuning (audio-direction §3 M2 rows; M2-21): the tool loops follow the readouts, comms stress per notice. */
export const m2Schema = z.object({
  /** Fade in, fade out and parameter smoothing (s) of the four M2 tool loops. */
  loopAttack: positive,
  loopRelease: positive,
  loopSmoothing: positive,
  /** Mining beam: the level without and with contact; the playback rate follows laser power (min at 0, max at 1). */
  miningBeam: z.object({ gainIdle: z.number().min(0).max(2), gainContact: z.number().min(0).max(2), rateMin: z.number().min(0.25).max(4), rateMax: z.number().min(0.25).max(4), cutoffIdle: positive, cutoffContact: positive }),
  /** Fracture strain: sounds above `threshold` instability; level and rate rise with instability. */
  miningStrain: z.object({ threshold: unit, gain: z.number().min(0).max(2), rateMin: z.number().min(0.25).max(4), rateMax: z.number().min(0.25).max(4) }),
  /** Cutting beam: on an anchor, and spilling heat into the part (harsher, lower). */
  cuttingBeam: z.object({ gain: z.number().min(0).max(2), spillGain: z.number().min(0).max(2), rate: z.number().min(0.25).max(4), spillRate: z.number().min(0.25).max(4), cutoff: positive, spillCutoff: positive }),
  /** Tractor: searching, and holding a part. */
  tractor: z.object({ gain: z.number().min(0).max(2), heldGain: z.number().min(0).max(2), rate: z.number().min(0.25).max(4), heldRate: z.number().min(0.25).max(4), cutoff: positive }),
  /** Cues without their own recipe: the tetherstone shimmer (never explained, D-031) and a part breaking free. */
  tetherstone: cueSchema,
  partFreed: cueSchema,
  /** A contract completed, a hostile group arrives, a group is defeated (ship-computer tones). */
  contractCompleted: cueSchema,
  groupArrived: cueSchema,
  /** M10 P1e2: a moor grant (the `moorGranted` space event), in place of the bay clearance tone. */
  moorGranted: cueSchema,
  /** M10 P5a12c (review B2): the last of a boom hull's booms has cooled after an overheat (the overheat tone sounds once, on the first). */
  boomsCooled: cueSchema,
  /** Comms: Tethys Crew Finance's fixed voice seed, and the stress of each spoken notice (0..1). */
  comms: z.object({
    financeSeed: z.number().int().nonnegative(),
    clearanceGranted: unit,
    clearanceDenied: unit,
    clearanceExpired: unit,
    leasePaid: unit,
    paymentMissed: unit,
    collections: unit,
    repoWarning: unit,
  }),
})

export const sfxFileSchema = z.object({
  render: z.object({
    /** Pre-render rate (a live context at another rate resamples on playback). */
    sampleRate: z.number().int().min(22050).max(96000),
    /** Seed of every variation (and the per-play jitter). */
    seed: z.number().int().nonnegative(),
    /** Offline renders in flight at once (each renders on its own thread in Chromium). */
    concurrency: z.number().int().min(1).max(16),
    /** Fade applied to the last samples of every one-shot (click-free ends). */
    tailFadeSeconds: nonNegative.max(0.1),
    /** Render order: priority sounds first; any sound not listed follows in `SOUND_IDS` order. */
    order: z.array(soundIdSchema),
  }),
  engine: z.object({
    /** Simultaneous voices in total (audio-direction §2), loops included. */
    maxVoices: z.number().int().min(8).max(128),
    /** Voices reserved for the persistent loops (drive, boost, lock solid; M3: cruise hum; M2: mining beam, strain, cutting beam, tractor). */
    loopVoices: z.number().int().min(0).max(8),
    /** Master gain at masterVolume 1. */
    masterTrim: z.number().min(0).max(2),
    /** Seconds for volume and setting changes to settle. */
    volumeSmoothing: positive,
    /** A stolen voice fades out over this long before the new play starts. */
    stealFadeSeconds: z.number().min(0).max(0.05),
    /** Bus trims at volume 1 (the settings multiply them). */
    buses: z.object({ music: z.number().min(0).max(2), sfx: z.number().min(0).max(2), ui: z.number().min(0).max(2), comms: z.number().min(0).max(2) }),
    /** Music ducking (audio-direction §4.6): under comms, and briefly under big impacts. */
    ducking: z.object({
      commsDb: db,
      impactDb: db,
      impactSeconds: positive,
      /** Plays of a `duck` sound at least this loud (after distance) count as big impacts. */
      impactMinGain: unit,
      attack: positive,
      release: positive,
    }),
    /** The hull chain for the player's own ship (audio-direction §2): heavy low end + cockpit convolution. */
    hull: z.object({
      lowShelfHz: positive,
      /** Low-shelf boost at hull intensity 1 (scales with the setting). */
      lowShelfDb: z.number().min(0).max(24),
      /** Low-pass at intensity 0 and at intensity 1. */
      lowpassHz: positive,
      lowpassMinHz: positive,
      q: positive,
      /** Convolution send at intensity 1, and the dry level. */
      wet: z.number().min(0).max(2),
      dry: z.number().min(0).max(2),
      /** The generated cockpit impulse response (ir.ts). */
      ir: z.object({ seconds: positive.max(2), predelay: nonNegative.max(0.1), decay: positive, lowpassHz: positive, modes: z.array(positive).max(12) }),
    }),
    master: z.object({
      /** Night mode (audio-direction §6): heavy compression of the whole mix, with make-up gain. */
      night: compressorSchema.extend({ makeupDb: z.number().min(0).max(24) }),
      /** The peak limiter before the −1 dBTP ceiling. */
      limiter: compressorSchema,
      /** Safety clipper ceiling (dBFS, 4× oversampled soft knee). */
      ceilingDb: z.number().min(-12).max(0),
      /** Where the clipper's soft knee starts, as a fraction of the ceiling. */
      kneeStart: z.number().min(0.3).max(0.99),
    }),
  }),
  /** Near-band attenuation (inverse, clamped; silent from `maxDistance`). */
  falloff: z
    .object({ refDistance: positive, rolloff: positive, maxDistance: positive, minGain: unit })
    .refine((f) => f.maxDistance > f.refDistance, { message: 'maxDistance must exceed refDistance' }),
  /** 3D panning and distance colour relative to the floating-origin camera. */
  spatial: z.object({
    /** Full sounds inside this distance (m); the distant band (thumps + ticks) beyond, up to `falloff.maxDistance`. */
    nearMaxDistance: positive,
    panWidth: unit,
    /** Low-pass at the reference distance and at `nearMaxDistance` (log interpolation between). */
    lowpassNearHz: positive,
    lowpassFarHz: positive,
    /** Sources behind the camera get their low-pass scaled by this (a subtle "behind you" darkening). */
    rearLowpassScale: z.number().min(0.1).max(1),
    /** Pan/filter changes smaller than this are not re-sent to the audio thread. */
    updateThreshold: nonNegative,
  }),
  /** The distant band (2–20 km): muffled thumps and a directional sensor tick. */
  distant: z.object({
    /** Thump gain at `nearMaxDistance` and at `falloff.maxDistance` (log interpolation between). */
    gainNear: z.number().min(0).max(2),
    gainFar: z.number().min(0).max(2),
    lowpassHz: positive,
    tick: z.object({ sound: soundIdSchema, gain: z.number().min(0).max(2), minInterval: nonNegative }),
  }),
  lockTone: z
    .object({
      intervalStart: positive,
      intervalEnd: positive,
      gain: z.number().min(0).max(2),
      /** The beep rises by this fraction of its pitch as the lock builds ("rising lock tone"). */
      pitchRise: z.number().min(0).max(1),
    })
    .refine((l) => l.intervalEnd <= l.intervalStart, { message: 'intervalEnd must not exceed intervalStart' }),
  /** The player's own ship: main-drive rumble, RCS puffs, boost pressure (shipSounds.ts). */
  ship: z.object({
    drive: z.object({
      idle: unit,
      full: z.number().min(0).max(2),
      boost: z.number().min(0).max(2),
      rateMin: z.number().min(0.25).max(2),
      rateMax: z.number().min(0.25).max(2),
      lowpassMin: positive,
      lowpassMax: positive,
      smoothing: positive,
    }),
    rcs: z.object({
      /** |thrust| above which an axis fires a puff, and below which it re-arms. */
      threshold: unit,
      release: unit,
      minInterval: nonNegative,
      gain: z.number().min(0).max(2),
      /** Stereo offset of a lateral puff. */
      pan: unit,
    }),
    boostLoop: z.object({ gain: z.number().min(0).max(2), attack: positive, release: positive }),
  }),
  /** Ship-computer alerts (cockpit, clean). */
  alarms: z.object({
    hullLowFraction: unit,
    hullLowRepeatSeconds: positive,
    shieldDownMinInterval: nonNegative,
    incomingLockMinInterval: nonNegative,
    gain: z.number().min(0).max(2),
  }),
  /** Explosions whose size hint (m) is at least this use the large explosion. */
  largeExplosionSize: positive,
  /** M2 tool loops and routing (src/audio/m2/route.ts; M2-21). */
  m2: m2Schema,
  sounds: z.object(Object.fromEntries(SOUND_IDS.map((id) => [id, soundDefSchema])) as Record<SoundId, typeof soundDefSchema>),
  ui: z.object({ uiClick: uiSoundSchema }),
})

export type SoundDef = z.infer<typeof soundDefSchema>
export type RecipeDef = z.infer<typeof recipeSchema>
export type MixDef = z.infer<typeof mixSchema>
export type SfxConfig = z.infer<typeof sfxFileSchema>
export type EngineConfig = SfxConfig['engine']
export type FalloffConfig = SfxConfig['falloff']
export type SpatialConfig = SfxConfig['spatial']
export type DistantConfig = SfxConfig['distant']
export type LockToneConfig = SfxConfig['lockTone']
export type ShipSoundConfig = SfxConfig['ship']
export type AlarmConfig = SfxConfig['alarms']
export type M2SoundConfig = SfxConfig['m2']

/** Parses and validates sfx data; throws with the Zod path on bad data. */
export function parseSfxConfig(raw: unknown): SfxConfig {
  const cfg = sfxFileSchema.parse(raw)
  for (const id of SOUND_IDS) {
    const d = cfg.sounds[id].mix.distant
    if (d !== null && cfg.sounds[d.sound].mix.distant !== null) {
      throw new Error(`sfx.json: sounds.${id}.mix.distant.sound "${d.sound}" must not itself map to a distant sound`)
    }
  }
  if (cfg.engine.loopVoices >= cfg.engine.maxVoices) throw new Error('sfx.json: engine.loopVoices must leave one-shot voices')
  if (cfg.spatial.nearMaxDistance >= cfg.falloff.maxDistance) throw new Error('sfx.json: spatial.nearMaxDistance must be below falloff.maxDistance')
  return cfg
}

/** The bundled `sfx.json`, parsed once. Mutable so the audition page can tune it live. */
export const sfxConfig: SfxConfig = parseSfxConfig(sfxJson)

/** Runtime index of a sound id, or -1. */
export function soundIndex(id: string): number {
  for (let i = 0; i < SOUND_IDS.length; i++) if (SOUND_IDS[i] === id) return i
  return -1
}

/** The sound defs in `SOUND_IDS` order. */
export function soundDefs(config: SfxConfig): SoundDef[] {
  return SOUND_IDS.map((id) => config.sounds[id])
}

/** Render order: `render.order` first (deduplicated), then every other sound in `SOUND_IDS` order. */
export function renderOrder(config: SfxConfig): number[] {
  const out: number[] = []
  for (const id of config.render.order) {
    const i = soundIndex(id)
    if (!out.includes(i)) out.push(i)
  }
  for (let i = 0; i < SOUND_IDS.length; i++) if (!out.includes(i)) out.push(i)
  return out
}
